'use client';

import { useQuery } from '@tanstack/react-query';
import { useConnection } from 'wagmi';
import { Address, formatUnits, parseUnits } from 'viem';
import { readContracts } from '@wagmi/core';
import { config } from '@/config/index';
import { ChainType, ChainId } from '@/constants/chain-specific';
import { DEX_LIST, DEX_LIST_BY_CHAIN } from '@/constants/project-specific/farms/dex';
import type { DexConfig } from '@/constants/project-specific/farms/types';
import type { RequiredAggregatorChainType } from '@/constants/project-specific';
import type { TokenConfigType } from '@/constants/types/currency';
import type { LiquidityPosition } from './useLiquidityPositions';
import { useTokenPrices, calculatePositionValue, calculatePriceRatio } from './useTokenPrices';
import { getPoolAddresses, getStaticPoolCount, getAllKnownPools } from '@/lib/pool-address-manager';
import { getTokenMetadataWithFallback, type CachedTokenInfo } from './useCachedTokenMetadata';

// Standard Uniswap V2 Factory ABI (minimal functions we need)
const FACTORY_ABI = [{
  inputs: [{
    type: 'uint256'
  }],
  name: 'allPairs',
  outputs: [{
    type: 'address'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'allPairsLength',
  outputs: [{
    type: 'uint256'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [{
    type: 'address'
  }, {
    type: 'address'
  }],
  name: 'getPair',
  outputs: [{
    type: 'address'
  }],
  stateMutability: 'view',
  type: 'function'
}] as const;

// Standard Uniswap V2 Pair ABI (minimal functions we need)
const PAIR_ABI = [{
  inputs: [{
    type: 'address'
  }],
  name: 'balanceOf',
  outputs: [{
    type: 'uint256'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'totalSupply',
  outputs: [{
    type: 'uint256'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'getReserves',
  outputs: [{
    type: 'uint112',
    name: '_reserve0'
  }, {
    type: 'uint112',
    name: '_reserve1'
  }, {
    type: 'uint32',
    name: '_blockTimestampLast'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'token0',
  outputs: [{
    type: 'address'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'token1',
  outputs: [{
    type: 'address'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'name',
  outputs: [{
    type: 'string'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'symbol',
  outputs: [{
    type: 'string'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'decimals',
  outputs: [{
    type: 'uint8'
  }],
  stateMutability: 'view',
  type: 'function'
}] as const;

// ERC20 ABI for token details
const ERC20_ABI = [{
  inputs: [],
  name: 'name',
  outputs: [{
    type: 'string'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'symbol',
  outputs: [{
    type: 'string'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'decimals',
  outputs: [{
    type: 'uint8'
  }],
  stateMutability: 'view',
  type: 'function'
}] as const;
interface PairInfo {
  pairAddress: Address;
  token0Address: Address;
  token1Address: Address;
  userBalance: bigint;
  totalSupply: bigint;
  reserve0: bigint;
  reserve1: bigint;
  dexSlug: string;
  dexName: string;
}
interface TokenInfo {
  address: Address;
  name: string;
  symbol: string;
  decimals: number;
}

/**
 * Get DEXs for a specific chain using the existing DEX_LIST_BY_CHAIN mapping
 */
function getDexesForChain(chainId: ChainType): Array<[string, DexConfig]> {
  const chainDexes = DEX_LIST_BY_CHAIN[chainId as RequiredAggregatorChainType];
  if (!chainDexes) {
    console.log(`No DEXs configured for chain ${chainId}`);
    return [];
  }

  // Convert DexConfig array back to [slug, config] tuples by finding the matching slug
  const result: Array<[string, DexConfig]> = [];
  chainDexes.forEach((dexConfig: DexConfig) => {
    // Find the slug for this dex config
    const entry = Object.entries(DEX_LIST).find(([_, config]) => config === dexConfig);
    if (entry) {
      const [slug] = entry;
      result.push([slug, dexConfig]);
    }
  });
  return result;
}

/**
 * Hook to fetch user liquidity positions by querying factory contracts directly
 */
export function useFactoryPositions(chainId: ChainType = 109, address?: Address) {
  const {
    address: connectedAddress
  } = useConnection();
  const {
    data: positionsData = {
      positions: [],
      tokenAddresses: []
    },
    isLoading: positionsLoading,
    error: error_0,
    refetch
  } = useQuery({
    queryKey: ['factoryPositions', address, chainId],
    queryFn: async (): Promise<{
      positions: LiquidityPosition[];
      tokenAddresses: Address[];
    }> => {
      if (!address) return {
        positions: [],
        tokenAddresses: []
      };
      const positions: LiquidityPosition[] = [];
      const tokenAddresses: Set<Address> = new Set();
      try {
        // Get all DEXs for this chain that support V2-compatible pools using the official DEX_LIST_BY_CHAIN mapping
        const chainDexes = getDexesForChain(chainId);
        const supportedDexs = chainDexes.filter(([dexSlug, dexInfo]) => {
          // Filter by DEX type - V2 and velodrome-type (velodrome uses V2-style liquidity pools)
          const isV2Compatible = dexInfo.type === 'v2' || dexInfo.type === 'velodrome' || !dexInfo.type; // Default to V2 if no type specified

          if (!isV2Compatible) {
            console.log(`Skipping DEX ${dexSlug} - type ${dexInfo.type} not supported (V2-compatible only)`);
          }
          return isV2Compatible;
        });
        console.log(`Found ${supportedDexs.length} V2-compatible DEXs available on chain ${chainId}:`, supportedDexs.map(([slug, info]) => `${info.name} (${slug})`).join(', '));

        // Process each DEX
        for (const [dexSlug_0, dexInfo_0] of supportedDexs) {
          try {
            const factoryAddress = dexInfo_0.factory.address;
            console.log(`Fetching positions for ${dexInfo_0.name} (${factoryAddress})`);

            // Get all pairs with user balances
            const userPairs = await getUserPairsFromFactory(factoryAddress, address ? address : connectedAddress as Address, chainId, dexSlug_0, dexInfo_0.name);
            console.log(`Found ${userPairs.length} positions for ${dexInfo_0.name}`);
            if (userPairs.length > 0) {
              // Collect all unique token addresses for batch lookup
              const uniqueTokenAddrs = new Set<Address>();
              for (const pairInfo of userPairs) {
                uniqueTokenAddrs.add(pairInfo.token0Address);
                uniqueTokenAddrs.add(pairInfo.token1Address);
              }

              // Batch fetch token metadata (cache-first with RPC fallback)
              console.log(`Fetching metadata for ${uniqueTokenAddrs.size} unique tokens...`);
              const tokenMetadataMap = await getTokenMetadataWithFallback(Array.from(uniqueTokenAddrs), chainId);

              // Convert pair info to liquidity positions using cached metadata
              for (const pairInfo_0 of userPairs) {
                const position = await convertPairToPosition(pairInfo_0, chainId, tokenMetadataMap);
                if (position) {
                  positions.push(position);
                  tokenAddresses.add(position.token0.address as Address);
                  tokenAddresses.add(position.token1.address as Address);
                }
              }
            }
          } catch (dexError) {
            console.error(`Error fetching positions from ${dexInfo_0.name}:`, dexError);
          }
        }
        console.log(`Total positions found: ${positions.length}`);
        return {
          positions,
          tokenAddresses: Array.from(tokenAddresses)
        };
      } catch (error) {
        console.error('Error fetching factory positions:', error);
        throw error;
      }
    },
    enabled: !!address,
    staleTime: 60000,
    // 1 minute
    retry: 2
  });

  // Get token prices for position valuation
  const {
    prices,
    isLoading: pricesLoading
  } = useTokenPrices(positionsData.tokenAddresses, chainId);

  // Calculate position values with prices
  const positionsWithValues = positionsData.positions.map(position_0 => {
    const token0Price = prices[position_0.token0.address as Address]?.priceUsd || 0;
    const token1Price = prices[position_0.token1.address as Address]?.priceUsd || 0;
    const totalValueUsd = calculatePositionValue(position_0.token0AmountFormatted, position_0.token1AmountFormatted, token0Price, token1Price);

    // Calculate current price from reserves (if available)
    let currentPrice = '0';
    if (position_0.token0Amount && position_0.token1Amount) {
      // Since token amounts are already in decimal format, just calculate ratio directly
      const token0Amount = parseFloat(position_0.token0Amount);
      const token1Amount = parseFloat(position_0.token1Amount);
      if (token0Amount > 0) {
        currentPrice = (token1Amount / token0Amount).toFixed(6);
      }
    }
    return {
      ...position_0,
      totalValueUsd,
      range: {
        ...position_0.range,
        currentPrice
      },
      // Mock APR calculation - in real implementation, this would be based on historical fees
      apr: totalValueUsd > 0 ? Math.random() * 30 + 5 : 0 // 5-35% mock APR
    };
  });
  return {
    positions: positionsWithValues,
    isLoading: positionsLoading || pricesLoading,
    error: error_0,
    refetch
  };
}

/**
 * Get all pairs from a factory where user has a balance
 *
 * OPTIMIZED: Uses static pool addresses + memory cache to avoid re-fetching
 * known pool addresses. Only queries the factory for new pools.
 */
async function getUserPairsFromFactory(factoryAddress: Address, userAddress: Address, chainId: ChainType, dexSlug: string, dexName: string): Promise<PairInfo[]> {
  try {
    const staticCount = getStaticPoolCount(chainId, dexSlug);
    console.log(`${dexName}: ${staticCount} pools in static cache`);

    // Get all pool addresses (static + cached + fetch any new ones)
    const pairAddresses = await getPoolAddresses(factoryAddress, chainId, dexSlug);
    const totalPairs = pairAddresses.length;
    console.log(`${dexName}: ${totalPairs} total pools (${totalPairs - staticCount} new since last update)`);
    if (totalPairs === 0) return [];

    // Batch size for multicall (adjust based on RPC limits)
    const BATCH_SIZE = 250;
    const userPairs: PairInfo[] = [];

    // Process pairs in batches - only need to check balances now (addresses already known)
    for (let i = 0; i < totalPairs; i += BATCH_SIZE) {
      const batchEnd = Math.min(i + BATCH_SIZE, totalPairs);
      const batchAddresses = pairAddresses.slice(i, batchEnd);
      console.log(`${dexName}: Checking balances for pools ${i} to ${batchEnd - 1}`);

      // Check user balances for these pairs
      const balanceContracts = batchAddresses.map(pairAddr => ({
        address: pairAddr,
        abi: PAIR_ABI,
        functionName: 'balanceOf' as const,
        args: [userAddress],
        chainId
      }));
      const balanceResults = await readContracts(config, {
        contracts: balanceContracts
      });

      // Find pairs where user has a balance
      const pairsWithBalance: Address[] = [];
      for (let j = 0; j < balanceResults.length; j++) {
        const result = balanceResults[j];
        if (result.status === 'success' && result.result as bigint > 0n) {
          pairsWithBalance.push(batchAddresses[j]);
        }
      }
      if (pairsWithBalance.length === 0) continue;
      console.log(`${dexName}: Found ${pairsWithBalance.length} pairs with user balance in batch`);

      // Get detailed info for pairs with balance
      const detailContracts = pairsWithBalance.flatMap(pairAddr => [
      // User balance
      {
        address: pairAddr,
        abi: PAIR_ABI,
        functionName: 'balanceOf' as const,
        args: [userAddress],
        chainId
      },
      // Total supply
      {
        address: pairAddr,
        abi: PAIR_ABI,
        functionName: 'totalSupply' as const,
        chainId
      },
      // Token addresses
      {
        address: pairAddr,
        abi: PAIR_ABI,
        functionName: 'token0' as const,
        chainId
      }, {
        address: pairAddr,
        abi: PAIR_ABI,
        functionName: 'token1' as const,
        chainId
      },
      // Reserves
      {
        address: pairAddr,
        abi: PAIR_ABI,
        functionName: 'getReserves' as const,
        chainId
      }]);
      const detailResults = await readContracts(config, {
        contracts: detailContracts
      });

      // Parse results and create PairInfo objects
      for (let j = 0; j < pairsWithBalance.length; j++) {
        const baseIdx = j * 5;
        const pairAddress = pairsWithBalance[j];
        const userBalance = detailResults[baseIdx];
        const totalSupply = detailResults[baseIdx + 1];
        const token0 = detailResults[baseIdx + 2];
        const token1 = detailResults[baseIdx + 3];
        const reserves = detailResults[baseIdx + 4];
        if (userBalance.status === 'success' && totalSupply.status === 'success' && token0.status === 'success' && token1.status === 'success' && reserves.status === 'success' && userBalance.result as bigint > 0n) {
          const [reserve0, reserve1] = reserves.result as [bigint, bigint, number];
          userPairs.push({
            pairAddress,
            token0Address: token0.result as Address,
            token1Address: token1.result as Address,
            userBalance: userBalance.result as bigint,
            totalSupply: totalSupply.result as bigint,
            reserve0,
            reserve1,
            dexSlug,
            dexName
          });
        }
      }
    }
    return userPairs;
  } catch (error) {
    console.error(`Error getting user pairs from ${dexName}:`, error);
    throw error;
  }
}

/**
 * Convert pair information to a LiquidityPosition
 *
 * @param pairInfo - The pair information from the factory
 * @param chainId - The chain ID
 * @param tokenMetadataMap - Pre-fetched token metadata (from cache or RPC)
 */
async function convertPairToPosition(pairInfo: PairInfo, chainId: ChainType, tokenMetadataMap: Map<Address, CachedTokenInfo>): Promise<LiquidityPosition | null> {
  try {
    // Get token info from pre-fetched metadata
    const token0Cached = tokenMetadataMap.get(pairInfo.token0Address);
    const token1Cached = tokenMetadataMap.get(pairInfo.token1Address);
    const token0Info: TokenInfo = token0Cached ? {
      address: token0Cached.address,
      name: token0Cached.name,
      symbol: token0Cached.symbol,
      decimals: token0Cached.decimals
    } : {
      address: pairInfo.token0Address,
      name: 'Unknown',
      symbol: 'UNK',
      decimals: 18
    };
    const token1Info: TokenInfo = token1Cached ? {
      address: token1Cached.address,
      name: token1Cached.name,
      symbol: token1Cached.symbol,
      decimals: token1Cached.decimals
    } : {
      address: pairInfo.token1Address,
      name: 'Unknown',
      symbol: 'UNK',
      decimals: 18
    };

    // Calculate user's share of the pool using BigInt arithmetic for precision
    const userToken0Amount = pairInfo.reserve0 * pairInfo.userBalance / pairInfo.totalSupply;
    const userToken1Amount = pairInfo.reserve1 * pairInfo.userBalance / pairInfo.totalSupply;

    // Format amounts for display
    const token0AmountFormatted = formatUnits(userToken0Amount, token0Info.decimals);
    const token1AmountFormatted = formatUnits(userToken1Amount, token1Info.decimals);

    // Convert to string for storage (avoiding potential precision issues)
    const userToken0AmountNum = Number(userToken0Amount) / 10 ** token0Info.decimals;
    const userToken1AmountNum = Number(userToken1Amount) / 10 ** token1Info.decimals;

    // Create token config objects
    const token0: TokenConfigType = {
      name: token0Info.name,
      symbol: token0Info.symbol,
      address: token0Info.address,
      decimals: token0Info.decimals,
      logo: `/images/tokens/${token0Info.symbol.toLowerCase()}.png` // Placeholder
    };
    const token1: TokenConfigType = {
      name: token1Info.name,
      symbol: token1Info.symbol,
      address: token1Info.address,
      decimals: token1Info.decimals,
      logo: `/images/tokens/${token1Info.symbol.toLowerCase()}.png` // Placeholder
    };

    // Create liquidity position
    const position: LiquidityPosition = {
      id: `${pairInfo.dexSlug}-${pairInfo.pairAddress}-${chainId}`,
      dex: pairInfo.dexSlug,
      dexName: pairInfo.dexName,
      token0,
      token1,
      liquidity: pairInfo.userBalance.toString(),
      liquidityFormatted: formatUnits(pairInfo.userBalance, 18),
      token0Amount: userToken0AmountNum.toString(),
      token1Amount: userToken1AmountNum.toString(),
      token0AmountFormatted,
      token1AmountFormatted,
      fees0: '0',
      // V2 doesn't track unclaimed fees separately
      fees1: '0',
      fees0Formatted: '0',
      fees1Formatted: '0',
      totalValueUsd: 0,
      // TODO: Calculate using price feeds
      feesValueUsd: 0,
      apr: 0,
      // TODO: Calculate based on historical data
      range: {
        tickLower: -887220,
        // V2 is full range
        tickUpper: 887220,
        currentTick: 0,
        inRange: true,
        // V2 is always in range
        lowerPrice: '0',
        upperPrice: '∞',
        currentPrice: '0' // TODO: Calculate from reserves
      },
      version: 'v2',
      chainId,
      contractAddress: pairInfo.pairAddress,
      pair: pairInfo.pairAddress,
      pairAddress: pairInfo.pairAddress
    };
    return position;
  } catch (error) {
    console.error('Error converting pair to position:', error);
    return null;
  }
}